/**
 * Centralized API Configuration
 * Ensures consistent API base URL and authentication across all services
 */

export interface ApiConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
}

class ApiConfigService {
  private config: ApiConfig;

  constructor() {
    this.config = this.initializeConfig();
  }

  private initializeConfig(): ApiConfig {
    // Prefer explicit API URL from environment.
    // This is required for preview deployments where Vercel protection can
    // block same-origin /api rewrites before they reach backend.
    const explicitBaseURL = process.env.REACT_APP_API_URL?.trim();
    const baseURL = explicitBaseURL && /^https?:\/\//i.test(explicitBaseURL)
      ? explicitBaseURL
      : '/api';

    if (process.env.NODE_ENV !== 'production') {
      const source = explicitBaseURL ? 'REACT_APP_API_URL' : 'same-origin /api';
      // eslint-disable-next-line no-console
      console.info(`[apiConfig] Resolved API base URL: ${baseURL} (source: ${source})`);
    }
    
    return {
      baseURL,
      timeout: 30000, // 30 seconds
      retryAttempts: 3
    };
  }

  getConfig(): ApiConfig {
    return { ...this.config };
  }

  getBaseURL(): string {
    return this.config.baseURL;
  }

  getTimeout(): number {
    return this.config.timeout;
  }

  getRetryAttempts(): number {
    return this.config.retryAttempts;
  }
}

export const apiConfig = new ApiConfigService();
